import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Target, Phone, Mail, CheckCircle2, X, Clock, AlertTriangle,
  Loader2, RefreshCw, TrendingUp, Calendar, MessageSquare, Copy,
} from "lucide-react";

const CAD = (v) =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v || 0);
const CAD2 = (v) =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 2 }).format(v || 0);

const URGENCY = {
  today: { label: "🔥 Aujourd'hui", color: "bg-red-500/20 text-red-300 border-red-500/40" },
  this_week: { label: "📅 Cette semaine", color: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  next_week: { label: "⏳ Semaine prochaine", color: "bg-zinc-700/40 text-zinc-300 border-zinc-700" },
};

const SOURCE_LABEL = {
  pending_inscription: "Inscription en attente",
  suspended_recent: "Contrat suspendu < 30j",
  suspended_old: "Contrat suspendu > 30j",
  trial_lead: "Lead cours d'essai (19,98 $)",
  funnel_lead_recent: "Funnel lead récent",
  funnel_lead_old: "Funnel lead ancien",
  boutique_lead: "Lead boutique",
  payment_followup: "Paiement à encaisser",
  review_contract: "Contrat à investiguer",
  coach_lead: "Lead coach",
};

const STATUS_COLORS = {
  pending: "bg-zinc-700/40 text-zinc-300",
  in_progress: "bg-blue-500/20 text-blue-300",
  completed: "bg-emerald-500/20 text-emerald-300",
  lost: "bg-red-500/20 text-red-300",
};


export default function RevenuePlanModule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completeModal, setCompleteModal] = useState(null);
  const [messageModal, setMessageModal] = useState(null);

  const generateMessage = async (action, channel) => {
    try {
      const r = await api.post(`/revenue-plan/generate-message`, {
        source: action.source,
        person: action.person,
        channel,
        discipline: action.discipline,
        estimated_revenue: action.estimated_revenue,
      });
      setMessageModal({ action, channel, ...r.data });
    } catch (e) {
      toast.error("Erreur génération message");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/revenue-plan/weekly?limit=15`);
      setData(r.data);
    } catch (e) {
      toast.error("Erreur chargement plan : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateAction = async (a, payload) => {
    try {
      await api.post(`/revenue-plan/action`, {
        source: a.source,
        person: a.person,
        ...payload,
      });
      toast.success("Action mise à jour");
      load();
    } catch (e) {
      toast.error("Erreur mise à jour");
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  }
  if (!data) return null;

  const k = data.kpis;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="revenue-plan-module">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-amber-100 tracking-tight flex items-center gap-2">
            <Target className="w-7 h-7 text-amber-400" /> Plan d'attaque revenu 7 jours
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Semaine du <span className="text-amber-300 font-mono">{data.week_of}</span> — Actions priorisées par valeur attendue × urgence.
          </p>
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-amber-400" data-testid="rp-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiBox label="Actions à exécuter" value={k.actions_pending} sub="pipeline cette semaine" />
        <KpiBox
          label="🎯 Revenu attendu (pondéré)"
          value={CAD(k.expected_revenue_this_week)}
          sub="× probabilité conversion"
          accent="amber"
        />
        <KpiBox label="Potentiel max" value={CAD(k.total_potential_revenue)} sub="si tout converti" />
        <KpiBox
          label="✓ Revenu verrouillé"
          value={CAD(k.actual_revenue_locked)}
          sub={`${k.actions_completed_this_week} actions complétées`}
          accent="emerald"
        />
      </div>

      {/* Source breakdown */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Pipeline par source</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.by_source).map(([src, v]) => (
            <div key={src} className="px-3 py-1.5 rounded-lg border border-zinc-800 bg-zinc-900 text-xs">
              <span className="text-zinc-300">{SOURCE_LABEL[src] || src}</span>
              <span className="text-zinc-500 mx-1">·</span>
              <span className="text-amber-300 font-mono">{v.count}</span>
              <span className="text-zinc-500 mx-1">→</span>
              <span className="text-emerald-300 font-mono">{CAD(v.expected)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action list */}
      <div className="space-y-3" data-testid="rp-actions">
        {data.actions.length === 0 && (
          <div className="text-center text-zinc-500 py-12">
            <p>🎉 Pipeline vide !</p>
            <p className="text-xs mt-2">Soit tu as tout converti, soit il faut aller chercher des leads.</p>
          </div>
        )}
        {data.actions.map((a, idx) => {
          const u = URGENCY[a.urgency] || URGENCY.this_week;
          return (
            <div
              key={`${a.source}-${a.person}-${idx}`}
              className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4 hover:bg-zinc-900/50 transition"
              data-testid={`rp-card-${idx}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-md font-semibold text-amber-100">{a.person}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${u.color}`}>{u.label}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{a.label}</span>
                    {a.status !== "pending" && (
                      <span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_COLORS[a.status]}`}>
                        {a.status}
                      </span>
                    )}
                    <span className="text-[10px] text-zinc-500">· {a.discipline}</span>
                  </div>

                  <div className="text-sm text-zinc-300 mb-2">{a.suggested_action}</div>

                  <div className="flex items-center gap-4 text-xs text-zinc-400">
                    {a.contact.email && (
                      <a href={`mailto:${a.contact.email}`} className="flex items-center gap-1 hover:text-amber-300" data-testid={`rp-email-${idx}`}>
                        <Mail className="w-3 h-3" /> {a.contact.email}
                      </a>
                    )}
                    {a.contact.phone && (
                      <a href={`tel:${a.contact.phone}`} className="flex items-center gap-1 hover:text-amber-300" data-testid={`rp-phone-${idx}`}>
                        <Phone className="w-3 h-3" /> {a.contact.phone}
                      </a>
                    )}
                  </div>

                  {a.notes && (
                    <div className="mt-2 text-xs text-emerald-300 bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1">
                      📝 {a.notes}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2 min-w-[180px]">
                  <div className="text-right">
                    <div className="text-xs text-zinc-500">Estimation</div>
                    <div className="text-lg font-bold text-amber-100">{CAD(a.estimated_revenue)}</div>
                    <div className="text-[10px] text-zinc-500">× {(a.conversion_probability * 100).toFixed(0)}%</div>
                    <div className="text-md font-semibold text-emerald-300">{CAD(a.expected_value)}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => updateAction(a, { status: "in_progress" })}
                      className={`px-2.5 py-1 rounded text-[11px] border transition ${
                        a.status === "in_progress"
                          ? "bg-blue-500 text-zinc-950 border-blue-500 font-semibold"
                          : "bg-blue-500/10 text-blue-300 border-blue-500/30 hover:bg-blue-500/20"
                      }`}
                      data-testid={`rp-in-progress-${idx}`}
                    >
                      <Clock className="w-3 h-3 inline" /> En cours
                    </button>
                    <button
                      onClick={() => setCompleteModal(a)}
                      className="px-2.5 py-1 rounded text-[11px] border bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20"
                      data-testid={`rp-complete-${idx}`}
                    >
                      <CheckCircle2 className="w-3 h-3 inline" /> Convertir
                    </button>
                    <button
                      onClick={() => updateAction(a, { status: "lost" })}
                      className="px-2.5 py-1 rounded text-[11px] border bg-red-500/10 text-red-300 border-red-500/30 hover:bg-red-500/20"
                      data-testid={`rp-lost-${idx}`}
                    >
                      <X className="w-3 h-3 inline" /> Perdu
                    </button>
                  </div>
                  <div className="flex gap-1.5">
                    {a.contact.email && (
                      <button
                        onClick={() => generateMessage(a, "email")}
                        className="px-2.5 py-1 rounded text-[11px] border bg-amber-500/10 text-amber-300 border-amber-500/30 hover:bg-amber-500/20"
                        data-testid={`rp-gen-email-${idx}`}
                      >
                        <Mail className="w-3 h-3 inline" /> Email
                      </button>
                    )}
                    {a.contact.phone && (
                      <button
                        onClick={() => generateMessage(a, "sms")}
                        className="px-2.5 py-1 rounded text-[11px] border bg-blue-500/10 text-blue-300 border-blue-500/30 hover:bg-blue-500/20"
                        data-testid={`rp-gen-sms-${idx}`}
                      >
                        <MessageSquare className="w-3 h-3 inline" /> SMS
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {completeModal && (
        <CompleteModal
          action={completeModal}
          onClose={() => setCompleteModal(null)}
          onSave={async (revenue, notes) => {
            await updateAction(completeModal, { status: "completed", actual_revenue: revenue, notes });
            setCompleteModal(null);
          }}
        />
      )}

      {messageModal && (
        <MessageModal
          data={messageModal}
          onClose={() => setMessageModal(null)}
        />
      )}

      <div className="text-xs text-zinc-500 text-center border-t border-zinc-800 pt-3">
        Sources : <code className="text-amber-400">pending_inscriptions</code>, <code className="text-amber-400">student_contracts</code> (suspendus + à réviser + paiements à encaisser),{" "}
        <code className="text-amber-400">funnel_leads</code>, <code className="text-amber-400">trial_leads</code>, <code className="text-amber-400">boutique_leads</code>.
        Priorisation par <em>Valeur Attendue = Revenu × Probabilité × Urgence</em>.
      </div>
    </div>
  );
}


function KpiBox({ label, value, sub, accent }) {
  const accentMap = {
    amber: "border-amber-500/30 from-amber-500/10",
    emerald: "border-emerald-500/30 from-emerald-500/10",
  };
  return (
    <div className={`rounded-2xl p-4 border bg-gradient-to-br to-zinc-900 ${accentMap[accent] || "border-zinc-800 from-zinc-900"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-bold text-amber-100 mt-1">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}


function CompleteModal({ action, onClose, onSave }) {
  const [revenue, setRevenue] = useState(action.estimated_revenue);
  const [notes, setNotes] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-zinc-950 border border-amber-500/30 rounded-2xl p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-emerald-300">✓ Convertir : {action.person}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-amber-300"><X className="w-5 h-5" /></button>
        </div>
        <label className="block mb-3">
          <span className="text-xs text-zinc-400">Revenu réellement encaissé (CAD)</span>
          <input
            type="number"
            value={revenue}
            onChange={(e) => setRevenue(Number(e.target.value))}
            className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 text-sm"
            data-testid="rp-complete-revenue"
          />
        </label>
        <label className="block mb-3">
          <span className="text-xs text-zinc-400">Notes (optionnel)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg p-2 text-zinc-100 text-sm min-h-[80px]"
            data-testid="rp-complete-notes"
          />
        </label>
        <button
          onClick={() => onSave(Number(revenue), notes)}
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold py-2 rounded-lg"
          data-testid="rp-complete-save"
        >
          Verrouiller ce revenu
        </button>
      </div>
    </div>
  );
}


function MessageModal({ data, onClose }) {
  const [subject, setSubject] = useState(data.subject || "");
  const [body, setBody] = useState(data.body || "");
  const copyAll = () => {
    const content = data.channel === "email" ? `Sujet : ${subject}\n\n${body}` : body;
    navigator.clipboard.writeText(content);
    toast.success("Copié dans le presse-papiers");
  };
  const sendNow = () => {
    if (data.channel === "email") {
      const mailto = `mailto:${data.action.contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
    } else {
      const sms = `sms:${data.action.contact.phone}?body=${encodeURIComponent(body)}`;
      window.location.href = sms;
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-zinc-950 border border-amber-500/30 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="message-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-amber-100 flex items-center gap-2">
            {data.channel === "email" ? <Mail className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
            {data.channel === "email" ? "Email personnalisé" : "SMS"} pour {data.action.person}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-amber-300"><X className="w-5 h-5" /></button>
        </div>
        {data.channel === "email" && (
          <label className="block mb-3">
            <span className="text-xs text-zinc-400">Sujet</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 text-sm"
              data-testid="msg-subject"
            />
          </label>
        )}
        <label className="block mb-3">
          <span className="text-xs text-zinc-400">{data.channel === "email" ? "Corps" : "Message"}</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-zinc-100 text-sm font-mono"
            style={{ minHeight: data.channel === "email" ? "300px" : "120px" }}
            data-testid="msg-body"
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button
            onClick={copyAll}
            className="px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm flex items-center gap-1"
            data-testid="msg-copy"
          >
            <Copy className="w-4 h-4" /> Copier
          </button>
          <button
            onClick={sendNow}
            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm flex items-center gap-1"
            data-testid="msg-send"
          >
            {data.channel === "email" ? "📧 Ouvrir dans Mail" : "📱 Envoyer SMS"}
          </button>
        </div>
        <div className="text-xs text-zinc-500 mt-3 italic">
          💡 Astuce : Modifie le template avant d'envoyer pour ajouter une touche personnelle.
        </div>
      </div>
    </div>
  );
}
